import type { ComponentStyleConfig } from "@chakra-ui/theme";
import { textStyles } from "theme/text-styles";
import { remCalc } from "theme/utils";

export const Button: ComponentStyleConfig = {
  baseStyle: {
    border: "1px solid",
    borderColor: "transparent",
    borderRadius: "base",
    fontFamily: "heading",
    fontWeight: "semibold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transitionDuration: "0.15s",
    _focus: {
      outline: "none !important",
    },
    _active: {
      outline: "none !important",
    },
    _focusVisible: {
      borderColor: "transparent",
      boxShadow: "outline",
    },
    _disabled: {
      opacity: 0.7,
      pointerEvents: "none",
    },
  },
  sizes: {
    sm: {
      ...textStyles.caption2,
      borderRadius: "14px",
      height: remCalc(36),
      minW: remCalc(36),
      px: "smaller",
      "&[data-left-icon]": {
        pl: "smallest",
      },
      "&[data-right-icon]": {
        pr: "smallest",
      },
    },
    md: {
      ...textStyles.caption1,
      borderRadius: "base",
      height: remCalc(44),
      minW: remCalc(44),
      px: "smaller",
      py: "smallest",
      "&[data-left-icon]": {
        pl: "smallest",
      },
      "&[data-right-icon]": {
        pr: "smallest",
      },
    },
    lg: {
      ...textStyles.body3,
      borderRadius: "base",
      height: remCalc(48),
      minW: remCalc(48),
      px: "small",
      "&[data-left-icon]": {
        pl: "smaller",
      },
      "&[data-right-icon]": {
        pr: "smaller",
      },
    },
  },
  variants: {
    primary: {
      borderColor: "brand.accent.primary",
      bg: "brand.accent.primary",
      color: "brand.accent.compliment",
      "&[data-pulse]": {
        // Active state inner pulse
        _before: {
          content: "''",
          pos: "absolute",
          inset: "-3px",
          borderRadius: "base",
          bg: "white",
          filter: "blur(4px)",
          opacity: 0,
          transition: "opacity 0.15s ease-out",
          zIndex: -1,
        },
        // Active state outer pulse
        _after: {
          content: "''",
          pos: "absolute",
          top: "-17px",
          right: "-11px",
          bottom: "-17px",
          left: "-11px",
          borderRadius: "28px",
          bg: "new.green.50",
          filter: "blur(8px)",
          opacity: 0,
          transition: "opacity 0.15s ease-out",
          zIndex: -2,
        },
      },
      _hover: {
        borderColor: "new.green.150",
        bg: "new.green.150",
        "&[data-pulse='light']": {
          // Hide pulse on mobile screens
          _before: {
            opacity: { base: 0, md: 1 },
          },
          _after: {
            opacity: { base: 0, md: 1 },
          },
        },
        "&[data-pulse='dark']": {
          // Hide pulse on mobile screens
          _before: {
            opacity: { base: 0, md: 0.2 },
          },
          _after: {
            opacity: { base: 0, md: 0.2 },
          },
        },
      },
      _active: {
        borderColor: "new.green.50",
      },
      _focusVisible: {
        bg: "new.green.150",
        borderColor: "white",
      },
    },
    secondary: {
      borderColor: "brand.border.light",
      bg: "white",
      color: "brand.text.light",
      // Buttons with icons will have the icon color updated on hover
      // Whereas `IconButtons` keep their color unchanged
      "&[data-left-icon], &[data-right-icon]": {
        svg: {
          color: "brand.textSecondary.light",
        },
      },
      _hover: {
        borderColor: "brand.textSecondary.light",
        color: "brand.text.light",
      },
      _active: {
        borderColor: "brand.text.light",
      },
    },
    darkBlue: {
      borderColor: "new.blue.950",
      bg: "new.blue.950",
      color: "white",
      // Buttons with icons will have white icons
      "&[data-left-icon], &[data-right-icon]": {
        svg: {
          color: "white",
        },
      },
      _hover: {
        borderColor: "new.blue.900",
        bg: "new.blue.900",
        color: "white",
      },
      _active: {
        borderColor: "new.blue.700",
        bg: "new.blue.700",
      },
      _focusVisible: {
        bg: "new.blue.900",
        borderColor: "white",
      },
    },
    directional: {
      py: "tiny",
      whiteSpace: "normal",
    },
    sidebar: {
      ...textStyles.caption1,
      borderRadius: "tiny",
      color: "brand.textSecondary.light",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      fontWeight: "normal",
      height: "auto",
      textAlign: "left",
      whiteSpace: "normal",
      my: "tiniest",
      px: "tiny",
      py: "tinier",
      transition: "all 0.15s ease-out",
      _hover: { bg: "brand.backgroundSecondary.light" },
      "&[data-active='true']": {
        bg: "new.green.50",
        color: "brand.text.light",
      },
    },
    unstyled: {
      p: 0,
      height: "auto",
      fontWeight: "base",
      textAlign: "left",
      whiteSpace: "initial",
      borderRadius: "tinier",
    },
  },
  defaultProps: {
    size: "md",
    variant: "primary",
  },
};
